import { yupResolver } from "@hookform/resolvers/yup";
import { InOutValidationSchema } from "../../utils/validations/validations";
import { useForm } from "react-hook-form";
import InputSearchableDropdown from "./InputSearchableDropdown";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getPartDropdownOptions, getParties } from "../../api/inventory";
import LoadingComponent from "../../LoadingComponent";
import {
  InOutModalTitleState,
  inOutResetFieldsState,
  toastBgState,
  toastMessageState,
  toastShowState,
  toggleBarcodeSearchState,
} from "../../utils/atoms/atoms";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { useEffect, useMemo, useRef, useState } from "react";
import { addInOut, editInOut, getBarcodeDetails } from "../../api/inout";
import { closeModal } from "../../utils/funcs/common";
import { IncomingItemsDetails } from "../../interfaces/app_types";
import { inOutStatus } from "../../utils/dropdown-values";
import FormField from "../common/FormField";

const InOutFormBody = () => {
  const resolver = yupResolver(InOutValidationSchema);

  const [currentStatus, setCurrentStatus] = useState<number>(0);
  const [isFindingBarcode, setIsFindingBarcode] = useState(false);
  const [isBarcodeScanned, setIsBarcodeScanned] = useState(false);
  const [barcodeDisabled, setBarcodeDisabled] = useState(true);

  const inOutModalTitle = useRecoilValue(InOutModalTitleState);
  const setToastShow = useSetRecoilState(toastShowState);
  const setToastBg = useSetRecoilState(toastBgState);
  const setToastMessage = useSetRecoilState(toastMessageState);
  const [inOutResetFields, setInOutResetFields] = useRecoilState(
    inOutResetFieldsState
  );
  const setToggleBarcodeSearch = useSetRecoilState(toggleBarcodeSearchState);

  const barcodeInputRef = useRef<HTMLInputElement | null>(null);
  const queryClient = useQueryClient();

  const { data: inOut } = useQuery<any>(["in-out-item"]);
  const isEditForm = inOutModalTitle === "Edit Entry";

  const {
    isLoading: isPartsLoading,
    data: inventory,
    isError: isPartError,
    isSuccess: isPartSuccess,
  } = useQuery({
    queryKey: ["part-dropdown-values"],
    queryFn: getPartDropdownOptions,
  });

  const {
    isLoading: isPartiesLoading,
    data: partiesOptions,
    isError: isPartiesError,
    isSuccess: isPartiesSuccess,
  } = useQuery({
    queryKey: ["parties"],
    queryFn: getParties,
  });

  const part_id = inventory?.filter((inv) => inv.value === inOut?.part_id)[0];
  const remarks = inOut?.remarks;
  const action_date = inOut?.action_date;
  const quantity = inOut?.quantity;
  const party = partiesOptions?.filter((party) => {
    return party.value == inOut?.party_id;
  })[0];
  const status = inOutStatus?.filter(
    (status) => status.value === inOut?.status
  )[0];

  const defaultValues = useMemo(
    () => ({
      part_id: part_id,
      remarks: remarks ?? "",
      action_date: action_date ?? "",
      party: party,
      status: status,
      quantity: quantity ?? 0,
      barcode: "",
    }),
    [part_id, party, status, inOut]
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset,
    getValues,
    resetField,
    setValue,
  } = useForm<IncomingItemsDetails>({
    resolver,
    defaultValues,
  });

  // Properly destructure register to merge refs safely — this fixes the barcode undefined bug
  const { ref: barcodeRegisterRef, ...barcodeRegisterRest } =
    register("barcode");

  const showToast = (bg: string, message: string) => {
    setToastBg(bg);
    setToastMessage(message);
    setToastShow(true);
  };

  const addInOutMutation = useMutation({
    mutationFn: (data: any) => addInOut(data),
    onSuccess() {
      queryClient.invalidateQueries(["in-out"]);
      queryClient.invalidateQueries(["stock-data"]);
      closeModal("inOutModal");
      showToast("success", "Entry added");
    },
  });

  const editInventoryMutation = useMutation({
    mutationFn: (data: any) => editInOut(inOut?.entry_id, data),
    onSuccess() {
      queryClient.invalidateQueries(["in-out"]);
      queryClient.invalidateQueries(["stock-data"]);
      closeModal("inOutModal");
      showToast("success", "Entry edited");
    },
  });

  const getBarcodeDetailsMutation = useMutation({
    mutationFn: (data: { barcode: string; status: number }) =>
      getBarcodeDetails(data.barcode, data.status),
    onSuccess(data) {
      reset({
        part_id: { label: data?.part_name, value: data?.part_id },
        remarks: "",
        action_date: "",
        party: {},
        quantity: data?.available_qty ?? 1,
        status: getValues("status"),
        barcode: getValues("barcode"),
      });
      setIsFindingBarcode(false);
      setIsBarcodeScanned(true);
      showToast("success", "Barcode synced");
    },
    onError(error: any) {
      setIsFindingBarcode(false);
      setIsBarcodeScanned(false);
      setValue("barcode", "");
      resetField("part_id");
      resetField("quantity");
      showToast("danger", error.response.data.error);
    },
  });

  const isLoading = isPartsLoading || isPartiesLoading; // fixed: || not &&
  const isError = isPartError || isPartiesError; // fixed: || not &&
  const isSuccess = isPartSuccess && isPartiesSuccess;

  const resetFieldsExceptStatus = () => {
    reset({
      part_id: {},
      remarks: "",
      action_date: "",
      party: {},
      quantity: 0,
      barcode: "",
      status: getValues("status"),
    });
  };

  const startScanning = () => {
    setBarcodeDisabled(false);
    setIsBarcodeScanned(false);
    resetField("barcode");
    barcodeInputRef.current?.focus();
  };

  const handleBarcodeKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.currentTarget.blur();
      setBarcodeDisabled(true);
      setToggleBarcodeSearch(true);
    }
  };

  const handleFindBarcode = () => {
    const barcodeValue = getValues("barcode");
    if (!barcodeValue) return;
    setBarcodeDisabled(true);
    setIsFindingBarcode(true);
    getBarcodeDetailsMutation.mutate({
      barcode: barcodeValue,
      status: getValues("status")?.value ?? 0,
    });
  };

  const handleStatusChange = (selectedStatus: {
    label: string;
    value: number;
  }) => {
    if (currentStatus === selectedStatus?.value) return;
    setCurrentStatus(selectedStatus?.value ?? 0);
    setIsBarcodeScanned(false);
    setBarcodeDisabled(false);
    resetFieldsExceptStatus();
  };

  const onSubmit = (data: IncomingItemsDetails) => {
    if (isEditForm) {
      editInventoryMutation.mutate(data);
    } else {
      addInOutMutation.mutate(data);
    }
  };

  useEffect(() => {
    if (inOutResetFields) {
      reset(defaultValues);
      setCurrentStatus(defaultValues?.status?.value ?? 0);
      setInOutResetFields(false);
    }
  }, [inOutResetFields]);

  return (
    <LoadingComponent
      isError={isError}
      isSuccess={isSuccess}
      isLoading={isLoading}
      ComponentBody={
        <div>
          <form onSubmit={handleSubmit(onSubmit)}>
            <InputSearchableDropdown
              name="status"
              selectedValue={status}
              handleSelectChange={handleStatusChange}
              options={inOutStatus}
              isAsync={true}
              control={control}
              isColor={true}
              errorMessage={errors?.status?.value?.message}
            />

            <div className="barcode-container m-2">
              <button
                type="button"
                onClick={startScanning}
                className="styled-btn--std m-1"
                disabled={currentStatus === 0}
              >
                Start Barcode Scan
              </button>

              <button
                type="button"
                onClick={() => {
                  setValue("barcode", "");
                  setBarcodeDisabled(false);
                  setIsBarcodeScanned(false);
                }}
                disabled={currentStatus === 0}
                className="styled-btn--std m-1"
              >
                Type
              </button>

              <button
                type="button"
                onClick={handleFindBarcode}
                disabled={
                  isBarcodeScanned || currentStatus === 0 || isFindingBarcode
                }
                className="styled-btn--dark m-1"
              >
                {isFindingBarcode ? "Finding..." : "Find"}
              </button>

              {/* Properly merged refs — this is the fix for barcode being undefined on submit */}
              <input
                type="text"
                disabled={barcodeDisabled}
                {...barcodeRegisterRest}
                ref={(el) => {
                  barcodeRegisterRef(el); // RHF tracks the value
                  barcodeInputRef.current = el; // local ref for focus management
                }}
                onKeyDown={handleBarcodeKeyDown}
              />
              <div className="fw-danger">{errors?.barcode?.message}</div>
            </div>

            <InputSearchableDropdown
              name="part_id"
              selectedValue={part_id}
              disabled={isBarcodeScanned} // enabled only after a successful scan
              options={inventory}
              isAsync={true}
              control={control}
              errorMessage={errors?.part_id?.value?.message}
            />

            <FormField
              title="Select Date"
              fieldType="date"
              value={inOut?.action_date}
              formRegister={{ ...register("action_date") }}
              errorMessage={errors?.action_date?.message}
            />

            <InputSearchableDropdown
              name="party"
              isAsync={true}
              isColor={true}
              isMulti={false}
              selectedValue={party}
              disabled={currentStatus !== 1}
              options={partiesOptions}
              control={control}
              errorMessage={errors?.party?.value?.message}
            />

            <FormField
              title="Quantity"
              errorMessage={errors?.quantity?.message}
              formRegister={{ ...register("quantity") }}
            />
            <FormField
              title="Remarks"
              formRegister={{ ...register("remarks") }}
              errorMessage={errors?.remarks?.message}
            />

            <button
              type="submit"
              className="styled-btn--dark--bold"
              disabled={
                addInOutMutation.isLoading || editInventoryMutation.isLoading
              }
            >
              {addInOutMutation.isLoading || editInventoryMutation.isLoading
                ? "Submitting..."
                : "Submit"}
            </button>
          </form>
        </div>
      }
    />
  );
};

export default InOutFormBody;
